import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, Shield, AlertTriangle, Zap, ExternalLink, RefreshCw, 
  Calendar, Newspaper, Bug, Globe, Loader2, AlertCircle
} from 'lucide-react';

// Serviço gratuito para converter RSS para JSON (sem necessidade de backend)
const RSS2JSON_API = 'https://api.rss2json.com/v1/api.json?rss_url=';

const SecurityInsights = () => {
  const [activeTab, setActiveTab] = useState('insights');
  const [news, setNews] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [threatStats, setThreatStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchRssFeed = async (feedUrl, source) => {
    try {
      const response = await fetch(`${RSS2JSON_API}${encodeURIComponent(feedUrl)}`);
      const data = await response.json();
      
      if (data.status === 'ok' && data.items) {
        return data.items.slice(0, 5).map(item => ({
          title: item.title || '',
          description: item.description ? item.description.replace(/<[^>]*>/g, '').substring(0, 200) : '',
          link: item.link || '',
          pub_date: item.pubDate || '',
          source: source,
          category: 'ameaca'
        }));
      }
      return [];
    } catch (error) {
      console.warn(`Erro ao buscar ${source}:`, error);
      return [];
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Buscar notícias de segurança diretamente dos RSS feeds
      const securityFeeds = [
        { url: 'https://feeds.feedburner.com/TheHackersNews', source: 'The Hacker News' },
        { url: 'https://www.bleepingcomputer.com/feed/', source: 'Bleeping Computer' },
        { url: 'https://krebsonsecurity.com/feed/', source: 'Krebs on Security' },
        { url: 'https://www.darkreading.com/rss.xml', source: 'Dark Reading' },
      ];

      const newsPromises = securityFeeds.map(feed => 
        fetchRssFeed(feed.url, feed.source)
      );

      const results = await Promise.allSettled(newsPromises);
      const allNews = results
        .filter(r => r.status === 'fulfilled')
        .flatMap(r => r.value || [])
        .slice(0, 15);

      setNews(allNews);
      
      // Vulnerabilidades estáticas (CVEs críticos recentes)
      setVulnerabilities([
        {
          cve_id: "CVE-2024-21762",
          description: "Fortinet FortiOS - Out-of-bound Write vulnerability permitindo execução remota de código sem autenticação",
          severity: "Crítica",
          cvss_score: 9.8,
          published_date: "2024-02-08",
          affected_products: "FortiOS 7.4.0-7.4.2, 7.2.0-7.2.6"
        },
        {
          cve_id: "CVE-2024-1709",
          description: "ConnectWise ScreenConnect - Authentication Bypass permitindo acesso administrativo não autorizado",
          severity: "Crítica",
          cvss_score: 10.0,
          published_date: "2024-02-19",
          affected_products: "ScreenConnect < 23.9.8"
        },
        {
          cve_id: "CVE-2024-27198",
          description: "JetBrains TeamCity - Authentication Bypass permitindo controle total do servidor CI/CD",
          severity: "Crítica",
          cvss_score: 9.8,
          published_date: "2024-03-04",
          affected_products: "TeamCity < 2023.11.4"
        },
        {
          cve_id: "CVE-2024-3400",
          description: "Palo Alto PAN-OS - Command Injection em GlobalProtect Gateway ativamente explorado",
          severity: "Crítica",
          cvss_score: 10.0,
          published_date: "2024-04-12",
          affected_products: "PAN-OS 10.2, 11.0, 11.1"
        },
        {
          cve_id: "CVE-2024-21887",
          description: "Ivanti Connect Secure - Command Injection em componente web permitindo RCE",
          severity: "Crítica",
          cvss_score: 9.1,
          published_date: "2024-01-10",
          affected_products: "Ivanti Connect Secure, Policy Secure"
        },
        {
          cve_id: "CVE-2024-23897",
          description: "Jenkins - Arbitrary File Read através de CLI permitindo vazamento de credenciais",
          severity: "Crítica",
          cvss_score: 9.8,
          published_date: "2024-01-24",
          affected_products: "Jenkins < 2.441, LTS < 2.426.3"
        },
        {
          cve_id: "CVE-2024-20353",
          description: "Cisco ASA/FTD - Denial of Service em VPN permitindo interrupção de serviço",
          severity: "Alta",
          cvss_score: 8.6,
          published_date: "2024-04-24",
          affected_products: "Cisco ASA, Firepower Threat Defense"
        }
      ]);
      
      // Estatísticas de ameaças (dados baseados em relatórios da indústria)
      setThreatStats({
        ransomware_increase: "+300%",
        avg_breach_cost: "R$ 6.5M",
        detection_time_days: 287,
        phishing_percentage: 95
      });
      
    } catch (error) {
      console.error('Error fetching security data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Static insights data
  const insights = [
    {
      id: 1,
      category: 'tendencia',
      icon: TrendingUp,
      title: 'Zero Trust Architecture',
      description: 'Modelo de segurança que assume que nenhum usuário ou dispositivo é confiável por padrão, exigindo verificação contínua.',
      impact: 'Alto',
      relevance: 'Empresas com trabalho remoto',
      color: 'from-blue-500 to-cyan-500',
      tags: ['Arquitetura', 'Acesso', '2025']
    },
    {
      id: 2,
      category: 'ameaca',
      icon: AlertTriangle,
      title: 'Ransomware como Serviço (RaaS)',
      description: 'Grupos criminosos oferecem ransomware como serviço, facilitando ataques mesmo por pessoas sem conhecimento técnico.',
      impact: 'Crítico',
      relevance: 'Todas as organizações',
      color: 'from-red-500 to-orange-500',
      tags: ['Ameaça', 'Ransomware', 'RaaS']
    },
    {
      id: 3,
      category: 'regulatorio',
      icon: Shield,
      title: 'LGPD e Multas Crescentes',
      description: 'ANPD intensifica fiscalização com multas que podem chegar a 2% do faturamento. Empresas precisam adequar processos urgentemente.',
      impact: 'Alto',
      relevance: 'Empresas brasileiras',
      color: 'from-emerald-500 to-teal-500',
      tags: ['LGPD', 'Compliance', 'Brasil']
    },
    {
      id: 4,
      category: 'tendencia',
      icon: Zap,
      title: 'IA na Detecção de Ameaças',
      description: 'Inteligência Artificial e Machine Learning revolucionam SOCs, detectando anomalias em tempo real com maior precisão.',
      impact: 'Transformacional',
      relevance: 'SOC e equipes de segurança',
      color: 'from-violet-500 to-purple-500',
      tags: ['IA', 'ML', 'SOC']
    },
    {
      id: 5,
      category: 'ameaca',
      icon: AlertTriangle,
      title: 'Ataques à Cadeia de Suprimentos',
      description: 'Hackers comprometem fornecedores para atingir grandes empresas. TPRM (gestão de risco de terceiros) torna-se essencial.',
      impact: 'Crítico',
      relevance: 'Empresas com fornecedores de TI',
      color: 'from-amber-500 to-orange-500',
      tags: ['Supply Chain', 'TPRM', 'Terceiros']
    },
    {
      id: 6,
      category: 'regulatorio',
      icon: Shield,
      title: 'ISO 27001:2022 - Nova Versão',
      description: 'Nova versão da ISO 27001 com 93 controles reorganizados. Empresas certificadas têm prazo para migração.',
      impact: 'Alto',
      relevance: 'Empresas certificadas ISO 27001',
      color: 'from-blue-500 to-indigo-500',
      tags: ['ISO 27001', 'Certificação', '2022']
    }
  ];

  const categories = [
    { id: 'all', label: 'Todos', count: insights.length },
    { id: 'tendencia', label: 'Tendências', count: insights.filter(i => i.category === 'tendencia').length },
    { id: 'ameaca', label: 'Ameaças', count: insights.filter(i => i.category === 'ameaca').length },
    { id: 'regulatorio', label: 'Regulatório', count: insights.filter(i => i.category === 'regulatorio').length },
  ];

  const filteredInsights = activeCategory === 'all' 
    ? insights 
    : insights.filter(i => i.category === activeCategory);

  const getImpactColor = (impact) => {
    switch(impact) {
      case 'Crítico': return 'bg-red-100 text-red-700';
      case 'Alto': return 'bg-orange-100 text-orange-700';
      case 'Transformacional': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getSeverityColor = (severity) => {
    if (severity === 'Crítica') return 'bg-red-500';
    if (severity === 'Alta') return 'bg-orange-500';
    return 'bg-yellow-500';
  };

  const tabs = [
    { id: 'insights', label: 'Insights', icon: TrendingUp },
    { id: 'news', label: 'Notícias', icon: Newspaper, count: news.length },
    { id: 'vulnerabilities', label: 'CVEs Críticos', icon: Bug, count: vulnerabilities.length },
  ];

  return (
    <section className="py-20 bg-white" data-testid="insights-section">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#10b981]/10 text-[#10b981] text-sm font-semibold rounded-full mb-4">
            <Globe className="w-4 h-4" />
            Central de Inteligência em Segurança
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
            Threat Intelligence Hub
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tendências, notícias em tempo real e vulnerabilidades críticas do cenário global de cybersegurança
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-[#1a3a52] text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#10b981]/10 text-[#10b981] hover:bg-[#10b981]/20 transition-colors text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>

        {/* Content based on active tab */}
        <AnimatePresence mode="wait">
          {/* INSIGHTS TAB */}
          {activeTab === 'insights' && (
            <motion.div
              key="insights"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Category Filter */}
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                      activeCategory === cat.id
                        ? 'bg-[#10b981] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {cat.label} ({cat.count})
                  </button>
                ))}
              </div>

              {/* Insights Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {filteredInsights.map((insight, index) => (
                  <motion.div
                    key={insight.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#10b981]/30 group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${insight.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <insight.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getImpactColor(insight.impact)}`}>
                        {insight.impact}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#1a3a52] mb-2 group-hover:text-[#10b981] transition-colors">
                      {insight.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {insight.description}
                    </p>
                    <div className="text-xs text-gray-500 mb-3">
                      <span className="font-semibold">Relevante para:</span> {insight.relevance}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {insight.tags.map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* NEWS TAB */}
          {activeTab === 'news' && (
            <motion.div
              key="news"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto"
            >
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#10b981]" />
                  <span className="ml-3 text-gray-600">Carregando notícias...</span>
                </div>
              ) : news.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhuma notícia disponível no momento</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {news.map((article, index) => (
                    <motion.a
                      key={index}
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="block bg-white rounded-xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-[#10b981]/30 group"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              article.category === 'ameaca' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {article.source}
                            </span>
                            {article.pub_date && (
                              <span className="text-xs text-gray-400">{article.pub_date}</span>
                            )}
                          </div>
                          <h3 className="text-lg font-bold text-[#1a3a52] group-hover:text-[#10b981] transition-colors mb-2">
                            {article.title}
                          </h3>
                          {article.description && (
                            <p className="text-sm text-gray-600 line-clamp-2">{article.description}</p>
                          )}
                        </div>
                        <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#10b981] flex-shrink-0" />
                      </div>
                    </motion.a>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* VULNERABILITIES TAB */}
          {activeTab === 'vulnerabilities' && (
            <motion.div
              key="vulnerabilities"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-4xl mx-auto"
            >
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <p className="text-sm text-red-800">
                    <strong>Atenção:</strong> CVEs críticos que exigem ação imediata. Verifique se sua organização está exposta.
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#10b981]" />
                </div>
              ) : (
                <div className="space-y-4">
                  {vulnerabilities.map((vuln, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded text-[#1a3a52]">
                              {vuln.cve_id}
                            </code>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${getSeverityColor(vuln.severity)}`}>
                              {vuln.severity}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">Publicado em {vuln.published_date}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-red-600">{vuln.cvss_score}</div>
                          <div className="text-xs text-gray-500">CVSS Score</div>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-3">{vuln.description}</p>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Produtos Afetados:</p>
                        <p className="text-sm text-gray-700 font-medium">{vuln.affected_products}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 bg-gradient-to-r from-[#1a3a52] to-[#2a5a72] rounded-2xl p-6 max-w-4xl mx-auto"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <p className="text-3xl font-bold text-[#10b981]">{threatStats?.ransomware_increase || '+300%'}</p>
              <p className="text-sm text-gray-300">Aumento em ransomware</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#10b981]">{threatStats?.avg_breach_cost || 'R$ 6.5M'}</p>
              <p className="text-sm text-gray-300">Custo médio de vazamento</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#10b981]">{threatStats?.detection_time_days || 287} dias</p>
              <p className="text-sm text-gray-300">Tempo para detectar breach</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#10b981]">{threatStats?.phishing_percentage || 95}%</p>
              <p className="text-sm text-gray-300">Ataques via phishing</p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4" />
            Dados atualizados automaticamente
          </p>
        </div>
      </div>
    </section>
  );
};

export default SecurityInsights;
